import React, { useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  DialogActions,
  Button,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';

import { useLazyRequest } from '../../hooks';
import { createSubsidy } from '../../api';
import { getHelperTextField, hasError } from '../../utils';
import { getApiErroMessage } from '../../utils/helpers';

import Input from '../Input';
import Select from '../Select';
import ButtonWithCirculaProgress from '../ButtonWithCirculaProgress';

import { TIME_OPTIONS, initialValues, validationSchema } from './utils';
import { formatInputAmount } from '../LimitsForm/utils';

const CreateSubsidyModal = ({ open = false, onClose, onCreateSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [, loadingCreateSubsidy, , executeCreateSubsidy] = useLazyRequest({
    request: createSubsidy,
    withPostSuccess: () => {
      enqueueSnackbar('El subsidio fue creado correctamente', {
        variant: 'success',
      });

      onClose();

      onCreateSuccess?.();
    },
    withPostFailure: (err) => {
      enqueueSnackbar(getApiErroMessage(err?.errorData), { variant: 'error' });
    },
  });

  const onCreateSubsidy = useCallback(
    (values) => {
      executeCreateSubsidy({
        address: values.address,
        alias: values.alias || null,
        time: values.time || null,
        amount: Number(values.amount) || null,
      });
    },
    [executeCreateSubsidy],
  );

  const { errors, touched, getFieldProps, submitForm, resetForm } = useFormik({
    initialValues,
    validationSchema,
    onSubmit: onCreateSubsidy,
  });

  useEffect(() => {
    if (!open) {
      resetForm();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <Dialog fullWidth maxWidth="xs" open={open}>
      <DialogTitle>Crear Subsidio</DialogTitle>

      <DialogContent>
        <Grid item container spacing={1}>
          <Grid item xs={12}>
            <Input
              name="address"
              label="Address"
              error={hasError(errors, touched, 'address')}
              helperText={getHelperTextField(errors, touched, 'address')}
              fullWidth
              size="small"
              autoComplete="off"
              {...getFieldProps('address')}
            />
          </Grid>

          <Grid item xs={12}>
            <Input
              name="amount"
              label="Monto (Opcional)"
              error={hasError(errors, touched, 'amount')}
              helperText={getHelperTextField(errors, touched, 'amount')}
              fullWidth
              size="small"
              autoComplete="off"
              {...getFieldProps('amount')}
              onKeyDown={formatInputAmount}
            />
          </Grid>

          <Grid item xs={12}>
            <Select
              name="time"
              label="Tiempo (Opcional)"
              error={hasError(errors, touched, 'time')}
              helperText={getHelperTextField(errors, touched, 'time')}
              fullWidth
              size="small"
              autoComplete="off"
              data={TIME_OPTIONS}
              {...getFieldProps('time')}
            />
          </Grid>

          <Grid item xs={12}>
            <Input
              name="alias"
              label="Alias (Opcional)"
              error={hasError(errors, touched, 'alias')}
              helperText={getHelperTextField(errors, touched, 'alias')}
              fullWidth
              size="small"
              autoComplete="off"
              {...getFieldProps('alias')}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="default" variant="outlined">
          Cancelar
        </Button>

        <ButtonWithCirculaProgress
          variant="outlined"
          color="primary"
          label="Crear"
          gutters={false}
          handleButtonClick={submitForm}
          loading={loadingCreateSubsidy}
        />
      </DialogActions>
    </Dialog>
  );
};

CreateSubsidyModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onCreateSuccess: PropTypes.func.isRequired,
};

export default CreateSubsidyModal;
